/* eslint-disable no-underscore-dangle */
import { useTranslate } from 'hooks/useTranslate';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IncentiveOfferOperationStatus } from 'modules/incentivesMarket/types';
import { getIncentiveOfferOperations } from 'modules/incentivesMarket/redux/incentiveOfferOperationActions';

import getTenderOfferRowData from './utils/getTenderOfferRowData';
import getIncentiveOperationRowData from './utils/getIncentiveOperationRowData';

import BeneficiaryTenderOffersTabRow from './components/BeneficiaryTenderOffersTabRow';
import BeneficiaryIncentiveOperationRow from './components/BeneficiaryIncentiveOperationRow';

const EXCLUDED_INCENTIVE_STATUSES = [
  IncentiveOfferOperationStatus.CANCELLED,
  IncentiveOfferOperationStatus.REJECTED,
];

function BeneficiaryTenderOffersTab({ society, beneficiaryAllPlans }) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const allTenderOffers = useSelector((state) => state.tenderOffers ?? []);

  const [incentiveOperations, setIncentiveOperations] = useState([]);

  const currentTenderOffers = useMemo(() => {
    if (!beneficiaryAllPlans?.length) {
      return [];
    }

    return beneficiaryAllPlans
      .map((b) =>
        b.tenderOffers?.map((tenderOffer) => ({
          tenderOfferData: tenderOffer,
          beneficiaryData: b,
        }))
      )
      ?.flat()
      ?.filter((b) => b?.tenderOfferData);
  }, [beneficiaryAllPlans]);

  const resolvedSocietyId = useMemo(
    () => String(society?.societyId ?? society?._id ?? ''),
    [society?.societyId, society?._id]
  );

  const beneficiaryIds = useMemo(() => {
    const ids =
      beneficiaryAllPlans
        ?.map((beneficiary) => {
          const rawId = beneficiary?._id ?? beneficiary?.id;
          return rawId ? String(rawId) : null;
        })
        .filter(Boolean) ?? [];

    return [...new Set(ids)];
  }, [beneficiaryAllPlans]);

  const beneficiaryIdsKey = beneficiaryIds.join(',');

  useEffect(() => {
    if (!resolvedSocietyId || !beneficiaryIdsKey) {
      setIncentiveOperations([]);
      return undefined;
    }

    let cancelled = false;
    const beneficiaryIdSet = new Set(
      beneficiaryIdsKey.split(',').filter(Boolean)
    );

    const fetchIncentiveOperations = async () => {
      const response = await dispatch(
        getIncentiveOfferOperations({
          societyId: resolvedSocietyId,
          seller: beneficiaryIdsKey,
          items: 100,
        })
      );

      if (cancelled) {
        return;
      }

      const operations = response?.data ?? [];
      const filtered = operations.filter((op) => {
        if (EXCLUDED_INCENTIVE_STATUSES.includes(op.status)) {
          return false;
        }

        const sellerId =
          typeof op.seller === 'string' ? op.seller : op.seller?._id;

        if (sellerId && beneficiaryIdSet.has(String(sellerId))) {
          return true;
        }

        return (op.planBreakdown || []).some((row) => {
          const breakdownBeneficiaryId =
            typeof row.beneficiary === 'string'
              ? row.beneficiary
              : row.beneficiary?._id;

          return (
            breakdownBeneficiaryId &&
            beneficiaryIdSet.has(String(breakdownBeneficiaryId))
          );
        });
      });

      setIncentiveOperations(filtered);
    };

    fetchIncentiveOperations();

    return () => {
      cancelled = true;
    };
  }, [dispatch, resolvedSocietyId, beneficiaryIdsKey]);

  const rows = useMemo(() => {
    const tenderRows = currentTenderOffers.map(
      ({ tenderOfferData: tenderOffer, beneficiaryData: beneficiary }, index) => {
        const {
          units,
          totalPriceUnits,
          soldUnits,
          totalPriceSoldUnits,
          netAmount,
          status,
        } = getTenderOfferRowData({ tenderOffer, beneficiary, allTenderOffers });

        return (
          <BeneficiaryTenderOffersTabRow
            index={index + 1}
            key={tenderOffer._id}
            rowKey={tenderOffer._id}
            units={units}
            totalPriceUnits={totalPriceUnits}
            soldUnits={soldUnits}
            totalPriceSoldUnits={totalPriceSoldUnits}
            status={status}
            societyId={resolvedSocietyId}
            tenderOfferId={tenderOffer._id}
            beneficiaryId={beneficiary._id}
            retentionPercent={tenderOffer?.retentionPercent || 0}
            netAmount={netAmount}
          />
        );
      }
    );

    const incentiveRows = incentiveOperations.map((operation, index) => {
      const {
        units,
        totalPriceUnits,
        soldUnits,
        totalPriceSoldUnits,
        netAmount,
        retentionPercent,
        status,
      } = getIncentiveOperationRowData(operation);

      const sellerId =
        typeof operation.seller === 'string'
          ? operation.seller
          : operation.seller?._id;

      return (
        <BeneficiaryIncentiveOperationRow
          index={currentTenderOffers.length + index + 1}
          key={operation._id}
          rowKey={operation._id}
          units={units}
          totalPriceUnits={totalPriceUnits}
          soldUnits={soldUnits}
          totalPriceSoldUnits={totalPriceSoldUnits}
          status={status}
          societyId={resolvedSocietyId}
          operationId={operation._id}
          beneficiaryId={sellerId}
          retentionPercent={retentionPercent}
          netAmount={netAmount}
        />
      );
    });

    return [...tenderRows, ...incentiveRows];
  }, [currentTenderOffers, incentiveOperations, resolvedSocietyId]);

  const hasRows = rows.length > 0;

  return (
    <div className="card-inner">
      <div className="nk-block-head">
        <div className="nk-block-between g-3">
          <div className="nk-block-head-content">
            <h4 className="nk-block-title">{t('Transactions')}</h4>
            <div className="nk-block-des">
              <p>{t('TenderOffersAndTransactionsDetails')}</p>
            </div>
          </div>
        </div>
      </div>

      {hasRows ? (
        <div className="nk-block nk-block-lg">
          <div className="card card-bordered card-preview">
            <table className="table table-striped" id="consolidationTable">
              <thead>
                <tr>
                  <th scope="col" className="d-none d-md-table-cell">
                    {t('Nr')}
                  </th>
                  <th scope="col" className="d-none d-md-table-cell">
                    {t('Units')}
                  </th>
                  <th scope="col" className="d-none d-md-table-cell">
                    {t('OfferAmount')}
                  </th>
                  <th scope="col" className="text-center text-md-left">
                    {t('AmountSold')}
                  </th>
                  <th scope="col" className="d-none d-md-table-cell">
                    {t('%Retention')}
                  </th>
                  <th scope="col" className="d-none d-md-table-cell">
                    {t('NetAmount')}
                  </th>
                  <th scope="col" className="text-center text-md-left">
                    {t('SoldUnits')}
                  </th>
                  <th scope="col">{t('Status')}</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default BeneficiaryTenderOffersTab;
